import {
  Autocomplete,
  Button,
  CircularProgress,
  Grid2,
  TextField,
} from "@mui/material";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { GKModal } from "components";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axiosInstance from "utils/axiosInstance";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  period?: string;
}

const PAGE_SIZE = 50;
const LOADING_OPTION = { gcode: "__loading__", isLoadingRow: true };

const AddSchemeModal = ({ open, setOpen, period }: Props) => {
  const queryClient = useQueryClient();
  const [selectedScheme, setSelectedScheme] = useState<any>(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Wait until typing pauses before hitting the API
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Same call as the Company Master list, restricted to MF, 50 at a time
  const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["MF_SCHEMES", debouncedSearch],
      queryFn: ({ pageParam = 0 }) =>
        axiosInstance.get("/admin-instrument/", {
          params: {
            requireTotalCount: true,
            take: PAGE_SIZE,
            skip: pageParam,
            search: debouncedSearch,
            filter: JSON.stringify({ inst_type: ["MF"] }),
          },
        }),
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.length * PAGE_SIZE;
        return loaded < (lastPage?.data?.totalCount || 0) ? loaded : undefined;
      },
      keepPreviousData: true,
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Something went wrong");
      },
    });

  // Promote the picked scheme into the selected period's list
  const { mutate: addScheme, isLoading: isSubmitting } = useMutation({
    mutationFn: () =>
      axiosInstance.post(
        `/mf-screener/promotions`,
        { gcode: selectedScheme?.gcode },
        { params: { period } }
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["mf-funds-by-category"] });
      toast.success(response?.data?.message || "Scheme added");
      setOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const schemeList: any[] =
    data?.pages?.flatMap((page) => page?.data?.data || []) || [];

  // While the next 50 load, show a spinner row at the bottom of the list
  const options = isFetchingNextPage
    ? [...schemeList, LOADING_OPTION]
    : schemeList;

  // Load the next 50 when the dropdown is scrolled to the bottom
  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const listbox = event.currentTarget;
    const reachedBottom =
      listbox.scrollHeight - (listbox.scrollTop + listbox.clientHeight) <= 1;
    if (reachedBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle="Add Scheme"
      footer={
        <Grid2 container justifyContent="flex-end" gap={1}>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={!selectedScheme || !period || isSubmitting}
            onClick={() => addScheme()}
            startIcon={isSubmitting && <CircularProgress size={16} />}
          >
            Submit
          </Button>
        </Grid2>
      }
    >
      <Grid2 container spacing={1}>
        <Grid2 size={12}>
          <Autocomplete
            options={options}
            value={selectedScheme}
            loading={isFetching}
            onChange={(_, val) => setSelectedScheme(val)}
            // Results are already searched on the server; show them as they come
            filterOptions={(options) => options}
            onInputChange={(_, val, reason) => {
              // Ignore the label being filled in after a pick
              if (reason !== "reset") setSearchText(val);
            }}
            isOptionEqualToValue={(option, val) => option?.gcode === val?.gcode}
            getOptionLabel={(option) => option?.name || ""}
            getOptionDisabled={(option) => Boolean(option?.isLoadingRow)}
            renderOption={(props, option) =>
              option?.isLoadingRow ? (
                <li
                  {...props}
                  key={option.gcode}
                  style={{ justifyContent: "center", gap: 8, opacity: 1 }}
                >
                  <CircularProgress size={16} />
                  Loading...
                </li>
              ) : (
                <li {...props} key={option?.gcode}>
                  {option?.name}
                </li>
              )
            }
            ListboxProps={{ onScroll: handleScroll }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scheme"
                placeholder="Search scheme"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid2>
      </Grid2>
    </GKModal>
  );
};

export default AddSchemeModal;
